import type { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import type { User } from '@/types';

/** Identity that owns cached server data: the signed-in user within their organization. */
export const authIdentityOf = (user: Pick<User, 'id' | 'organization_id'> | null | undefined): string =>
  user ? `${user.id}|${user.organization_id}` : '';

/**
 * Drop every cached query whenever the signed-in identity changes: logout (clearAuth),
 * a different account signing in, or a switch of organization. Token refreshes and
 * profile updates of the same user keep the cache. Returns an unsubscribe function.
 */
export function bindQueryCacheToAuth(queryClient: QueryClient): () => void {
  let identity = authIdentityOf(useAuthStore.getState().user);
  return useAuthStore.subscribe((state) => {
    const next = authIdentityOf(state.user);
    if (next === identity) {
      return;
    }
    identity = next;
    void queryClient.cancelQueries();
    queryClient.clear();
  });
}
